import { inject, Injectable } from '@angular/core'
import { BreakPoint, LayoutBreakPoints, LayoutConfig } from '@spartacus/storefront'
import { fromEvent, Observable, of } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import { WindowService } from './window.service'
import { WindowRef } from '@spartacus/core'

export enum CARHARTT_BREAKPOINT {
  mobile = 'mobile',
  tablet = 'tablet',
  desktop = 'desktop'
}

export type CarharttLayoutBreakPoints = LayoutBreakPoints & {
  [t in CARHARTT_BREAKPOINT]?: number | BreakPoint;
};

@Injectable()
export class MediaQueryService {
  private breakpoints: CarharttLayoutBreakPoints
  private config: LayoutConfig = inject(LayoutConfig)
  private windowService: WindowService = inject(WindowService)
  private windowRef = inject(WindowRef)

  constructor () {
    this.breakpoints = {
      mobile: 425,
      tablet: 768,
      desktop: 1440,
      ...(this.config.breakpoints as CarharttLayoutBreakPoints)
    }
  }

  private activeMediaQueries: { [key: string]: Observable<boolean> } = {}

  getBreakpoint (name: CARHARTT_BREAKPOINT, type?: keyof BreakPoint): number | BreakPoint | undefined {
    const breakpoint = this.breakpoints[name] ?? undefined
    if (typeof breakpoint === 'object' && type) {
      return breakpoint[type] ?? breakpoint['min'] ?? breakpoint['max'] ?? 0
    }
    return breakpoint
  }

  isUp (breakPoint: CARHARTT_BREAKPOINT): Observable<boolean> {
    return this.mediaQuery('min', breakPoint)
  }

  isDown (breakPoint: CARHARTT_BREAKPOINT): Observable<boolean> {
    return this.mediaQuery('max', breakPoint)
  }

  mediaQuery (type: keyof BreakPoint, breakPoint: CARHARTT_BREAKPOINT): Observable<boolean> {
    const mediaId = `${type}-${breakPoint}px`

    if (mediaId in this.activeMediaQueries) {
      return this.activeMediaQueries[mediaId]
    }

    if (!this.windowRef.isBrowser() || !this.windowService.nativeWindow?.matchMedia) {
      const noSSRFallback = of(false)
      this.activeMediaQueries[mediaId] = noSSRFallback
      return noSSRFallback
    }

    const breakpointValue = this.getBreakpoint(breakPoint, type)
    const mqText = `(${type}-width: ${breakpointValue}px)`
    const mediaQuery = this.windowService.nativeWindow.matchMedia(mqText)

    const dynamicMediaQuery = fromEvent<MediaQueryList>(mediaQuery, 'change').pipe(
      startWith(mediaQuery),
      map((query: MediaQueryList) => query.matches)
    )

    this.activeMediaQueries[mediaId] = dynamicMediaQuery
    return dynamicMediaQuery
  }
}
